'use client';

import { createContext, useContext, useState, useEffect, useCallback, useRef, ReactNode } from 'react';
import { useRouter } from 'next/navigation';
import { AdminSessionData } from '@/lib/adminSession';
import { csrfHeaders } from '@/lib/csrf';

export type LoginResult =
  | { status: 'ok' }
  | { status: 'requiere2FA' }
  | { status: 'requiereSetup2FA'; setupToken: string; secreto: string; qrDataUrl: string };

interface AdminSessionCtx {
  admin:       AdminSessionData['admin'] | null;
  authHeaders: Record<string, string>;
  hydrated:    boolean;
  login:       (email: string, password: string, codigo2FA?: string) => Promise<LoginResult>;
  completarSetup2FA: (setupToken: string, codigo: string) => Promise<void>;
  logout:      () => Promise<void>;
}

const Ctx = createContext<AdminSessionCtx | null>(null);

/** Lee el `exp` (segundos epoch) de un JWT sin validar la firma — solo para decidir cuándo refrescar. */
function leerExpiracion(token: string): number | null {
  try {
    const payload = JSON.parse(atob(token.split('.')[1].replace(/-/g, '+').replace(/_/g, '/')));
    return typeof payload.exp === 'number' ? payload.exp * 1000 : null;
  } catch {
    return null;
  }
}

export function AdminSessionProvider({ children }: { children: ReactNode }) {
  const [session,  setSession]  = useState<AdminSessionData | null>(null);
  const [hydrated, setHydrated] = useState(false);
  const router = useRouter();
  const refreshTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const logout = useCallback(async () => {
    if (refreshTimer.current) clearTimeout(refreshTimer.current);
    await fetch('/api/admin/logout', { method: 'POST' });
    setSession(null);
    router.push('/admin/login');
  }, [router]);

  const programarRefresh = useCallback((token: string) => {
    if (refreshTimer.current) clearTimeout(refreshTimer.current);
    const exp = leerExpiracion(token);
    if (!exp) return;
    const delay = Math.max(exp - Date.now() - 60_000, 5_000);
    refreshTimer.current = setTimeout(async () => {
      try {
        const r = await fetch('/api/admin/refresh', { method: 'POST' });
        if (!r.ok) { await logout(); return; }
        const { session: nueva } = await r.json();
        setSession(nueva);
        programarRefresh(nueva.token);
      } catch {
        await logout();
      }
    }, delay);
  }, [logout]);

  useEffect(() => {
    // Limpieza de versiones anteriores que guardaban el token en localStorage (XSS-vulnerable)
    localStorage.removeItem('mrcode_admin_token');

    fetch('/api/admin/session')
      .then(r => r.ok ? r.json() : null)
      .then(s => { setSession(s); if (s?.token) programarRefresh(s.token); })
      .catch(() => setSession(null))
      .finally(() => setHydrated(true));

    return () => { if (refreshTimer.current) clearTimeout(refreshTimer.current); };
  }, [programarRefresh]);

  async function login(email: string, password: string, codigo2FA?: string): Promise<LoginResult> {
    const res = await fetch('/api/admin/login', {
      method:  'POST',
      headers: { 'Content-Type': 'application/json', ...csrfHeaders() },
      body:    JSON.stringify({ email, password, codigo2FA }),
    });
    const data = await res.json();
    if (data.requiereSetup2FA) {
      return { status: 'requiereSetup2FA', setupToken: data.setupToken, secreto: data.secreto, qrDataUrl: data.qrDataUrl };
    }
    if (data.requiere2FA) {
      if (!res.ok) throw new Error(data.error ?? 'Código de verificación inválido');
      return { status: 'requiere2FA' };
    }
    if (!res.ok) throw new Error(data.error ?? 'Error al iniciar sesión');
    setSession(data.session);
    if (data.session?.token) programarRefresh(data.session.token);
    return { status: 'ok' };
  }

  async function completarSetup2FA(setupToken: string, codigo: string) {
    const res = await fetch('/api/admin/2fa-setup-verify', {
      method:  'POST',
      headers: { 'Content-Type': 'application/json', ...csrfHeaders() },
      body:    JSON.stringify({ setupToken, codigo }),
    });
    const data = await res.json();
    if (!res.ok) throw new Error(data.error ?? 'Código incorrecto');
    setSession(data.session);
    if (data.session?.token) programarRefresh(data.session.token);
  }

  const authHeaders: Record<string, string> = session ? { Authorization: `Bearer ${session.token}` } : {};

  return (
    <Ctx.Provider value={{ admin: session?.admin ?? null, authHeaders, hydrated, login, completarSetup2FA, logout }}>
      {children}
    </Ctx.Provider>
  );
}

export function useAdminSession() {
  const ctx = useContext(Ctx);
  if (!ctx) throw new Error('useAdminSession must be used inside AdminSessionProvider');
  return ctx;
}
